import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { SUSPENSE_ACCOUNT_PATH, suspenseAccountPath } from './conciliation.paths';
import { CONCILIATION_READ_PERMISSION } from './conciliation.screen';
import { MOVEMENT_FILTERS, SUSPENSE_LIST_FILTERS } from './suspense-account.filters';

// Manifestos do Bolsão (SuspenseAccount/**, router.tsx:1478-1488). Arquivo leve: entra no `src/app/screens.ts` e
// no ⌘K. Leitura com `Conciliation:Read` e tenant raiz, herdados da rota da conciliação. Só o Bolsão cobra
// `Conciliation:Update` no front para as ações (SuspenseAccountListContainer.tsx:45, DetailsContainer.tsx:42).

/** Gate de escrita do Bolsão: Novo Bolsão, Lançar valor, Remover valor e Editar. */
export const SUSPENSE_UPDATE_PERMISSION = can('Conciliation', 'Update');
const ACCESS = { rootTenancy: true } as const;
const emptyInput = z.strictObject({});

/** Ação que só existe com a tela montada: a tela troca o `run` pelo dela. */
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela do Bolsão para usar esta ação.`);
};

// ─── Lista ───────────────────────────────────────────────────────────────────────────────────────

export const suspenseAccountNew = action({
  id: 'conciliation.suspenseAccountNew',
  kind: 'draft',
  label: msg`Novo Bolsão`,
  description: msg`Abre o formulário de novo Bolsão: o cadastro da operação e o lançamento inicial. Nada é gravado sem a confirmação da pessoa.`,
  permission: SUSPENSE_UPDATE_PERMISSION,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'novo' }),
});

export const suspenseAccountFilter = filterAction('conciliation.suspenseAccountFilter', SUSPENSE_LIST_FILTERS);

export const suspenseAccountExport = action({
  id: 'conciliation.suspenseAccountExport',
  kind: 'read',
  label: msg`Exportar CSV`,
  description: msg`Exporta em CSV as contas Bolsão do filtro aplicado, cada uma com o extrato completo. Arquivo grande vai por e-mail.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const suspenseAccountOpen = action<{ id: string }>({
  id: 'conciliation.suspenseAccountOpen',
  kind: 'navigate',
  label: msg`Abrir Bolsão`,
  description: msg`Abre o saldo, o cadastro e o extrato de uma conta Bolsão da lista. Nada muda só por abrir.`,
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(suspenseAccountPath(input.id)),
});

export const suspenseAccountPage = listPageAction('conciliation.suspenseAccountPage');

export const suspenseAccountListScreen = defineScreen({
  id: 'conciliation.suspenseAccountList',
  path: SUSPENSE_ACCOUNT_PATH,
  title: msg`Bolsão`,
  description: msg`Contas Bolsão por operação de contrato: onde fica o valor que sobra dos repasses. Mostra o saldo atual de cada conta e o saldo total do filtro, com os filtros, a exportação e o cadastro de um novo Bolsão.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  place: { icon: 'landmark', parent: 'conciliation.dataprev' },
  actions: [suspenseAccountNew, suspenseAccountFilter, suspenseAccountExport, suspenseAccountOpen, suspenseAccountPage],
});

// ─── Detalhe ─────────────────────────────────────────────────────────────────────────────────────

export const suspenseAccountEntry = action({
  id: 'conciliation.suspenseAccountEntry',
  kind: 'draft',
  label: msg`Lançar valor`,
  description: msg`Abre o lançamento de entrada manual no Bolsão aberto, com o saldo atual e o saldo depois do lançamento. Nada é gravado sem a confirmação da pessoa.`,
  permission: SUSPENSE_UPDATE_PERMISSION,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'lancar' }),
});

export const suspenseAccountWithdrawal = action({
  id: 'conciliation.suspenseAccountWithdrawal',
  kind: 'draft',
  label: msg`Remover valor`,
  description: msg`Abre a remoção de valor do Bolsão aberto (devolução ou ajuste manual). Só debita o Bolsão; nenhuma cobrança muda. Nada é gravado sem a confirmação da pessoa.`,
  permission: SUSPENSE_UPDATE_PERMISSION,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'remover' }),
});

export const suspenseAccountEdit = action({
  id: 'conciliation.suspenseAccountEdit',
  kind: 'draft',
  label: msg`Editar`,
  description: msg`Abre a edição do cadastro do Bolsão aberto (nº da operação, CPF/CNPJ, cliente, corban e fundo). O saldo não muda por aqui. Nada é gravado sem a confirmação da pessoa.`,
  permission: SUSPENSE_UPDATE_PERMISSION,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'editar' }),
});

export const suspenseMovementsFilter = filterAction('conciliation.suspenseMovementsFilter', MOVEMENT_FILTERS);
export const suspenseMovementsPage = listPageAction('conciliation.suspenseMovementsPage');

export const suspenseAccountDetailScreen = defineScreen({
  id: 'conciliation.suspenseAccountDetail',
  path: `${SUSPENSE_ACCOUNT_PATH}/$id`,
  title: msg`Detalhes do Bolsão`,
  description: msg`Uma conta Bolsão: o resumo do saldo (entradas, saídas, movimentos e o último lançamento), o cadastro e o extrato com filtros, mais lançar valor, remover valor e editar o cadastro.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  // Mãe é a lista Dataprev, não a do Bolsão: a trilha tem no máximo 4 níveis (seção › hub › Dataprev › registro).
  place: { icon: 'landmark', parent: 'conciliation.dataprev', record: msg`Bolsão` },
  actions: [suspenseAccountEntry, suspenseAccountWithdrawal, suspenseAccountEdit, suspenseMovementsFilter, suspenseMovementsPage],
});

// ─── Contexto para o agente (U46 D2-C): o que a pessoa vê, sem documento nem nome ─────────────────

export function suspenseListContext(input: { readonly page: number; readonly total: number | undefined; readonly filters: number; readonly sheet: string | undefined }): ScreenContext {
  const { page, total, filters } = input;
  const parts = [t`Contas Bolsão, página ${page}.`, total === undefined ? t`Carregando.` : t`Contas encontradas: ${total}.`];
  if (filters > 0) parts.push(t`Filtros aplicados: ${filters}.`);
  if (input.sheet === 'novo') parts.push(t`Gaveta "Novo Bolsão" aberta.`);
  return { summary: parts.join(' '), fields: { page, total: total ?? null, filterCount: filters, newOpen: input.sheet === 'novo' }, sensitivity: 'internal' };
}

export function suspenseDetailContext(input: {
  readonly id: string;
  readonly loaded: boolean;
  readonly movements: number | undefined;
  readonly filters: number;
  readonly sheet: string | undefined;
}): ScreenContext {
  const entity = { type: 'SuspenseAccount', id: input.id };
  if (!input.loaded) return { summary: t`Bolsão carregando.`, entity, fields: {}, sensitivity: 'internal' };
  const { movements, filters } = input;
  const parts = [t`Bolsão aberto.`, movements === undefined ? '' : t`Movimentos no extrato: ${movements}.`, filters > 0 ? t`Filtros do extrato: ${filters}.` : ''];
  if (input.sheet === 'lancar') parts.push(t`Gaveta "Lançar valor" aberta.`);
  if (input.sheet === 'remover') parts.push(t`Gaveta "Remover valor" aberta.`);
  if (input.sheet === 'editar') parts.push(t`Gaveta "Editar Bolsão" aberta.`);
  return {
    summary: parts.filter(Boolean).join(' '),
    entity,
    fields: { movements: movements ?? null, filterCount: filters, sheet: input.sheet ?? null },
    sensitivity: 'internal',
  };
}
